import { useState, useEffect, useMemo, useRef, Fragment } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  useDiagnosticRoutingsPaginated,
  useAllDiagnosticRoutings,
  useActiveDiagnosticCategories,
  useUpdateRoutingStatus,
  useTrashDiagnosticRoutings,
} from '../../services/httpServices/queries';
import { usePermissions } from '../../hooks/usePermissions';
import { receiptItemsCategoryNames } from '../../utils/specimen';
import { joinFullName, patientInitials } from '../../utils/patientName';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { DatePicker } from '../../components/ui/DatePicker';
import {
  ClipboardList,
  Loader2,
  AlertCircle,
  ArrowRight,
  X,
  Search,
  CheckCircle,
  Plus,
  Eye,
  Pencil,
  SlidersHorizontal,
  Trash2,
  CalendarDays,
  Tag,
} from 'lucide-react';

const statusMeta: Record<
  string,
  { label: string; badge: 'warning' | 'secondary' | 'primary' | 'success' | 'danger' | 'default'; icon?: React.ReactNode }
> = {
  PENDING: { label: 'Pending', badge: 'warning' },
  WAITING: { label: 'Waiting', badge: 'secondary' },
  IN_PROGRESS: { label: 'In Progress', badge: 'primary' },
  COMPLETED: { label: 'Completed', badge: 'success', icon: <CheckCircle size={12} /> },
  CANCELLED: { label: 'Cancelled', badge: 'danger' },
};

const nextStatusMap: Record<string, string> = {
  PENDING: 'WAITING',
  WAITING: 'IN_PROGRESS',
  IN_PROGRESS: 'COMPLETED',
};

/**
 * Ordered lifecycle of a diagnostic order. The routing status is advanced
 * automatically as specimens are collected / received / completed (see
 * `advanceRouting` on the backend), so it doubles as the order's progress.
 */
const ORDER_STAGES: { key: string; label: string }[] = [
  { key: 'PENDING', label: 'Pending' },
  { key: 'WAITING', label: 'Waiting' },
  { key: 'IN_PROGRESS', label: 'In Progress' },
  { key: 'COMPLETED', label: 'Completed' },
];

/**
 * Compact per-order progress indicator derived from the order status only.
 * A cancelled order shows a stopped (red) bar instead of a stage position.
 */
function OrderProgress({ status }: { status: string }) {
  if (status === 'CANCELLED') {
    return (
      <div className="flex flex-col gap-1.5 min-w-[140px]">
        <div className="flex items-center gap-1">
          {ORDER_STAGES.map((stage, i) => (
            <Fragment key={stage.key}>
              <span className="h-2 w-2 rounded-full bg-border" title={stage.label} />
              {i < ORDER_STAGES.length - 1 && (
                <span className="h-0.5 flex-1 rounded bg-border" />
              )}
            </Fragment>
          ))}
        </div>
        <span className="text-[11px] font-medium text-danger">Cancelled</span>
      </div>
    );
  }

  const index = Math.max(
    0,
    ORDER_STAGES.findIndex((s) => s.key === status),
  );
  const percent = Math.round(((index + 1) / ORDER_STAGES.length) * 100);

  return (
    <div className="flex flex-col gap-1.5 min-w-[140px]">
      <div className="flex items-center gap-1">
        {ORDER_STAGES.map((stage, i) => (
          <Fragment key={stage.key}>
            <span
              className={`h-2 w-2 rounded-full ${i <= index ? 'bg-primary' : 'bg-border'}`}
              title={stage.label}
            />
            {i < ORDER_STAGES.length - 1 && (
              <span
                className={`h-0.5 flex-1 rounded ${i < index ? 'bg-primary' : 'bg-border'}`}
              />
            )}
          </Fragment>
        ))}
      </div>
      <span className="text-[11px] text-text-muted">
        <span className="font-medium text-text">{ORDER_STAGES[index].label}</span> · {percent}%
      </span>
    </div>
  );
}

export function Orders() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const limit = 20;

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);

  // Row selection for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  // Debounce the search input to avoid hammering the server on each keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const filters = useMemo(
    () => ({
      status: selectedStatus || undefined,
      categoryId: selectedCategory || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      search: debouncedSearch.trim() || undefined,
    }),
    [selectedStatus, selectedCategory, dateFrom, dateTo, debouncedSearch],
  );

  // Counts always ignore the status filter so the status cards stay meaningful
  // while a status is selected.
  const countsFilters = useMemo(() => ({ ...filters, status: undefined }), [filters]);

  const { data: categoriesData } = useActiveDiagnosticCategories();
  const { data, isLoading, error } = useDiagnosticRoutingsPaginated(page, limit, filters);
  const { data: countsData } = useAllDiagnosticRoutings(countsFilters);
  const updateMutation = useUpdateRoutingStatus();
  const trashMutation = useTrashDiagnosticRoutings();

  const routings = useMemo(() => data?.data || [], [data]);
  const meta = data?.meta;
  const categories = categoriesData?.data || [];

  const canManageRouting = hasPermission('diagnostics:routing:manage');

  // Reset to first page whenever the query changes.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, selectedStatus, selectedCategory, dateFrom, dateTo]);

  // Drop selected ids that are no longer visible (e.g. after paging/filtering).
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(routings.map((r: any) => r.id));
      const next = new Set<string>();
      let changed = false;
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [routings]);

  const allSelected = routings.length > 0 && routings.every((r: any) => selectedIds.has(r.id));
  const someSelected = routings.some((r: any) => selectedIds.has(r.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const everySelected = routings.length > 0 && routings.every((r: any) => prev.has(r.id));
      return everySelected ? new Set() : new Set(routings.map((r: any) => r.id));
    });
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {
      ALL: 0,
      PENDING: 0,
      WAITING: 0,
      IN_PROGRESS: 0,
      COMPLETED: 0,
      CANCELLED: 0,
    };
    const all = countsData?.data || [];
    counts.ALL = all.length;
    all.forEach((r: any) => {
      if (counts[r.status] !== undefined) counts[r.status]++;
    });
    return counts;
  }, [countsData]);

  const activeFilterCount =
    (selectedCategory ? 1 : 0) + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0);

  const hasActiveFilters =
    !!searchQuery || !!selectedStatus || activeFilterCount > 0;

  const clearFilters = () => {
    setSearchQuery('');
    setDebouncedSearch('');
    setSelectedStatus('');
    setSelectedCategory('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const handleStatusUpdate = async (routingId: string, currentStatus: string) => {
    const nextStatus = nextStatusMap[currentStatus];
    if (!nextStatus) return;
    await updateMutation.mutateAsync({
      routingId,
      data: { status: nextStatus },
    });
  };

  const handleCancel = async (routingId: string) => {
    await updateMutation.mutateAsync({
      routingId,
      data: { status: 'CANCELLED' },
    });
  };

  const handleConfirmTrash = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      const res: any = await trashMutation.mutateAsync(ids);
      const trashed = res?.data?.trashed?.length ?? ids.length;
      setSelectedIds(new Set());
      toast.success(`Moved ${trashed} order${trashed === 1 ? '' : 's'} to trash`);
    } catch {
      toast.error('Failed to move orders to trash');
    } finally {
      setBulkConfirmOpen(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <ClipboardList className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-text">Orders</h1>
            <p className="text-xs text-text-muted">Diagnostic orders across all categories</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canManageRouting && (
            <Link
              to="/administration/orders/trash"
              className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
            >
              <Trash2 size={16} />
              Trash
            </Link>
          )}
          {hasPermission('diagnostics:receipt:create') && (
            <Link
              to="/opd/diagnostic-receipts/new"
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
            >
              <Plus size={16} />
              New Receipt
            </Link>
          )}
        </div>
      </div>

      {/* Status stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { key: 'ALL', label: 'Total', color: 'bg-text-muted/10 text-text-muted border-text-muted/20' },
          { key: 'PENDING', label: 'Pending', color: 'bg-warning/10 text-warning border-warning/20' },
          { key: 'WAITING', label: 'Waiting', color: 'bg-info/10 text-info border-info/20' },
          { key: 'IN_PROGRESS', label: 'In Progress', color: 'bg-primary/10 text-primary border-primary/20' },
          { key: 'COMPLETED', label: 'Completed', color: 'bg-success/10 text-success border-success/20' },
          { key: 'CANCELLED', label: 'Cancelled', color: 'bg-danger/10 text-danger border-danger/20' },
        ].map((stat) => (
          <button
            key={stat.key}
            onClick={() =>
              stat.key === 'ALL'
                ? setSelectedStatus('')
                : setSelectedStatus(selectedStatus === stat.key ? '' : stat.key)
            }
            className={`text-left p-3 rounded-xl border transition-all ${stat.color} ${
              selectedStatus === stat.key ? 'ring-2 ring-primary/30 shadow-sm scale-[1.02]' : 'hover:shadow-sm'
            }`}
          >
            <p className="text-lg font-bold">{statusCounts[stat.key] || 0}</p>
            <p className="text-xs font-medium opacity-90">{stat.label}</p>
          </button>
        ))}
      </div>

      {/* Search + Filter Bar */}
      <div className="bg-surface rounded-xl border border-border p-4 shadow-sm space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search by patient, receipt, or test..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className={`px-3 py-2 border rounded-lg text-sm font-medium flex items-center gap-1.5 whitespace-nowrap transition-colors ${
              showFilters || activeFilterCount > 0
                ? 'border-primary text-primary bg-primary/5'
                : 'border-border text-text-muted hover:text-text hover:bg-background'
            }`}
          >
            <SlidersHorizontal size={14} />
            Filter
            {activeFilterCount > 0 && (
              <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-white text-[10px] font-semibold">
                {activeFilterCount}
              </span>
            )}
          </button>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-text-muted mb-1">
                <Tag size={13} />
                Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="">All Categories</option>
                {categories.map((category: any) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-text-muted mb-1">
                <CalendarDays size={13} />
                From Date
              </label>
              <DatePicker value={dateFrom} onChange={setDateFrom} />
            </div>

            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-text-muted mb-1">
                <CalendarDays size={13} />
                To Date
              </label>
              <DatePicker value={dateTo} min={dateFrom || undefined} onChange={setDateTo} />
            </div>
          </div>
        )}
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && canManageRouting && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.size}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkConfirmOpen(true)}
              disabled={trashMutation.isPending}
              className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {trashMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Move to Trash
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={trashMutation.isPending}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load orders</span>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/60">
                  <th className="px-4 py-3 w-10 text-left">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={routings.length === 0}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      title="Select all"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-muted uppercase tracking-wide">
                    Receipt
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-muted uppercase tracking-wide">
                    Patient
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-muted uppercase tracking-wide">
                    Category
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-muted uppercase tracking-wide">
                    Tests
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-muted uppercase tracking-wide">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-muted uppercase tracking-wide">
                    Room
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-text-muted uppercase tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {routings.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-16 text-center">
                      <ClipboardList className="w-10 h-10 mx-auto mb-3 text-text-muted/40" />
                      <p className="text-sm font-medium text-text-muted mb-1">No orders found</p>
                      <p className="text-xs text-text-muted/70">
                        {hasActiveFilters
                          ? 'Try adjusting your search or filters'
                          : 'No diagnostic orders yet'}
                      </p>
                      {hasActiveFilters && (
                        <Button variant="outline" size="sm" className="mt-3" onClick={clearFilters}>
                          <X size={14} className="mr-1" />
                          Clear Filters
                        </Button>
                      )}
                    </td>
                  </tr>
                ) : (
                  routings.map((routing: any) => {
                    const receipt = routing.receipt;
                    const items =
                      receipt?.items?.filter(
                        (item: any) => item.diagnosticDepartmentId === routing.diagnosticDepartmentId,
                      ) || [];
                    const nextStatus = nextStatusMap[routing.status];
                    const statusInfo = statusMeta[routing.status] || {
                      label: routing.status,
                      badge: 'default',
                    };

                    return (
                      <tr key={routing.id} className="hover:bg-background/40 transition-colors group">
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(routing.id)}
                            onChange={() => toggleOne(routing.id)}
                            className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div
                            className="flex flex-col cursor-pointer"
                            onClick={() => navigate(`/diagnostics/routing/${routing.id}`)}
                          >
                            <span className="text-sm font-mono text-primary hover:underline">
                              {receipt?.receiptNumber}
                            </span>
                            <span className="text-xs text-text-muted">
                              ৳{receipt?.totalAmount?.toLocaleString()}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-semibold text-primary">
                                {patientInitials(receipt?.patient || {})}
                              </span>
                            </div>
                            <div className="flex flex-col">
                              <span className="text-sm font-medium text-text">
                                {joinFullName(receipt?.patient || {})}
                              </span>
                              <span className="text-xs text-text-muted">
                                {receipt?.patient?.mobile}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 text-sm text-text">
                            <Tag size={14} className="text-text-muted" />
                            {receiptItemsCategoryNames(items) || '—'}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1.5">
                            {items.map((item: any) => (
                              <Badge
                                key={item.id}
                                variant={
                                  item.status === 'COMPLETED'
                                    ? 'success'
                                    : item.status === 'REFUNDED'
                                      ? 'danger'
                                      : 'warning'
                                }
                              >
                                {item.testName}
                              </Badge>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-2">
                            <Badge variant={statusInfo.badge} className="gap-1 w-fit">
                              {statusInfo.icon}
                              {statusInfo.label}
                            </Badge>
                            <OrderProgress status={routing.status} />
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-text-muted">
                          {routing.roomNumber || (
                            <span className="text-text-muted/50 italic">N/A</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => navigate(`/diagnostics/routing/${routing.id}`)}
                              className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                              title="View Order"
                            >
                              <Eye size={16} />
                            </button>
                            <button
                              onClick={() => navigate(`/diagnostics/routing/${routing.id}`)}
                              className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                              title="Edit Order"
                            >
                              <Pencil size={16} />
                            </button>
                            {nextStatus && canManageRouting && (
                              <Button
                                size="sm"
                                onClick={() => handleStatusUpdate(routing.id, routing.status)}
                                disabled={updateMutation.isPending}
                                className="ml-1"
                              >
                                <ArrowRight size={12} className="mr-1" />
                                {nextStatus.replace('_', ' ')}
                              </Button>
                            )}
                            {routing.status !== 'CANCELLED' &&
                              routing.status !== 'COMPLETED' &&
                              canManageRouting && (
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() => handleCancel(routing.id)}
                                  disabled={updateMutation.isPending}
                                  className="ml-1"
                                >
                                  <X size={12} className="mr-1" />
                                  Cancel
                                </Button>
                              )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            totalPages={meta?.totalPages || 1}
            total={meta?.total || 0}
            limit={limit}
            onPageChange={setPage}
            itemName="orders"
          />
        </Card>
      )}

      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Move ${selectedIds.size} order${selectedIds.size === 1 ? '' : 's'} to trash?`}
        description="The selected orders will be moved to the trash. You can restore them later."
        confirmLabel={`Move ${selectedIds.size} to trash`}
        loading={trashMutation.isPending}
        onConfirm={handleConfirmTrash}
        onCancel={() => setBulkConfirmOpen(false)}
      />
    </div>
  );
}
